import { DeeplinkFeatureKey } from '$extensionSrc/components/DeeplinkFeatureFlowContextProvider'
import { useExtensionData } from '$extensionSrc/hooks/extensionStorageListener'
import { isValidRecommendationKey } from '$extensionSrc/recommendations/recommendationTypes'
import { PLATFORM, STORAGE_KEY } from '$extensionSrc/utils/enums'
import * as localStorage from '$extensionSrc/utils/localStorage'

export type DeeplinkFeatureKeys = Partial<Record<PLATFORM, DeeplinkFeatureKey>>

export function parseDeeplinkFeatureKeys(blob: any): DeeplinkFeatureKeys {
  if (!blob || typeof blob !== 'object') {
    return {}
  }

  const result: DeeplinkFeatureKeys = {}

  for (const [key, value] of Object.entries(blob)) {
    if (
      // Validate the key is a valid PLATFORM
      PLATFORM[key as keyof typeof PLATFORM] &&
      // Validate the value is a valid DeeplinkFeatureKey
      value !== null &&
      typeof value === 'string' &&
      (PLATFORM[value as keyof typeof PLATFORM] ||
        isValidRecommendationKey(value))
    ) {
      result[key as PLATFORM] = value as DeeplinkFeatureKey
    }
  }

  return result
}

export async function getDeeplinkFeatureKeys(): Promise<DeeplinkFeatureKeys> {
  const blob = await localStorage.getSingle(STORAGE_KEY.DEEPLINK_FEATURE_KEYS)
  return parseDeeplinkFeatureKeys(blob)
}

export async function getDeeplinkFeatureKeyForPlatform(
  platform: PLATFORM,
): Promise<DeeplinkFeatureKey | null> {
  const featureKeys = await getDeeplinkFeatureKeys()
  return featureKeys[platform] || null
}

export async function updateDeeplinkFeatureKey(
  platform: PLATFORM,
  key: DeeplinkFeatureKey,
): Promise<void> {
  const featureKeys = await getDeeplinkFeatureKeys()

  return localStorage.set({
    [STORAGE_KEY.DEEPLINK_FEATURE_KEYS]: {
      ...featureKeys,
      [platform]: key,
    },
  })
}

export async function clearDeeplinkFeatureKey(
  platform: PLATFORM,
): Promise<void> {
  const featureKeys = await getDeeplinkFeatureKeys()

  delete featureKeys[platform]

  return localStorage.set({
    [STORAGE_KEY.DEEPLINK_FEATURE_KEYS]: featureKeys,
  })
}

export function useDeeplinkFeatureKeys(): DeeplinkFeatureKeys | undefined {
  const { data, isLoaded } = useExtensionData({
    local: [STORAGE_KEY.DEEPLINK_FEATURE_KEYS],
  })

  if (!isLoaded || !data) {
    return undefined
  }

  return parseDeeplinkFeatureKeys(data[STORAGE_KEY.DEEPLINK_FEATURE_KEYS])
}
